import { siteApiSlice } from "../../app/siteApiSlice";
import NamedEntity from "../../interfaces/NamedEntity";

export interface ImportErrorResponse {
	resultMessage: string;
	errorMessage: string;
	dataProcessingSuccessful: false;
	loadedCoursesCount: number;
	loadedTeachersCount: number;
	loadedStudentsCount: number;
}

export interface AdminImportErrorBase {
	row: number;
	errors: string[];
	isValid: boolean;
}

export interface AdminImportCourse extends AdminImportErrorBase {
	grade?: number | null;
	name: string;
}

export interface AdminImportTeacher extends AdminImportErrorBase {
	fName: string;
	lName: string;
	email: string;
	phoneNumber: string;
	courses: string[];
}

export interface AdminImportStudentIdentification {
	id: number;
	fName: string;
	lName: string;
	studentId: string;
	username: string;
	dateOfBirth: string;
}

export enum Gender {
	Female = 367,
	Male = 368,
}

export interface AdminImportStudent extends AdminImportErrorBase {
	fName: string;
	lName: string;
	studentId: string;
	dateOfBirth: string;
	gradeLevel?: number | null;
	username: string;
	password: string;
	gender?: Gender | null;
	ethinicity: string;
	economicallyDisadvantaged?: boolean | null;
	limitedEnglish?: boolean | null;
	disability?: boolean | null;
	courses: string[];
	identifications: AdminImportStudentIdentification[];
	matchingUserId?: number | null;
}

export interface AdminImportCount {
	success: number;
	total: number;
}

export interface AdminImport {
	courses: AdminImportCourse[];
	teachers: AdminImportTeacher[];
	students: AdminImportStudent[];
	resultMessage: string;
	dataProcessingSuccessful: boolean;
	courseCount: AdminImportCount;
	teacherCount: AdminImportCount;
	studentCount: AdminImportCount;
}

export interface AdminImportResult {
	errorMessages: string[];
	inserts: number;
	updates: number;
	noChanges: number;
}

export interface AdminImportPreparationResult extends AdminImportResult {
	students: AdminImportStudent[];
	isIdentificationNeeded: boolean;
}

export const rosterImportApiSlice = siteApiSlice.injectEndpoints({
	endpoints: (build) => ({
		districtOptionsForRosterImport: build.query<NamedEntity[], void>({
			query: () => {
				return {
					url: `/AdminRosterImport/GetDistrictsForRosterImport`,
				};
			},
			providesTags: ["Districts", "Schools"],
		}),
		schoolOptionsForRosterImport: build.query<NamedEntity[], number | undefined>({
			query: (districtId) => {
				return {
					url: `/School/GetSchoolsByDistrictIdForRosterImport`,
					params: { districtId },
				};
			},
			providesTags: ["Districts", "Schools"],
		}),
		uploadFile: build.mutation<
			AdminImport | ImportErrorResponse,
			{ schoolId: number; file: File }
		>({
			query: ({ schoolId, file }) => {
				const formData = new FormData();
				formData.append("file", file);

				return {
					url: `AdminRosterImport/UploadFile?schoolId=${encodeURIComponent(schoolId)}`,
					method: "POST",
					body: formData,
				};
			},
		}),
		importCourses: build.mutation<
			AdminImportResult | ImportErrorResponse,
			{ schoolId: number; data: AdminImportCourse[] }
		>({
			query: ({ schoolId, data }) => ({
				url: `AdminRosterImport/ImportCourses?schoolId=${encodeURIComponent(schoolId)}`,
				method: "POST",
				body: data,
			}),
			invalidatesTags: [
				"District",
				"School",
				"Schools",
				"CourseInfo",
				"UserInfo",
				"UserList",
			],
		}),
		importTeachers: build.mutation<
			AdminImportResult | ImportErrorResponse,
			{ schoolId: number; data: AdminImportTeacher[] }
		>({
			query: ({ schoolId, data }) => ({
				url: `AdminRosterImport/ImportTeachers?schoolId=${encodeURIComponent(schoolId)}`,
				method: "POST",
				body: data,
			}),
			invalidatesTags: [
				"District",
				"School",
				"Schools",
				"CourseInfo",
				"UserInfo",
				"UserList",
			],
		}),
		prepareImportStudents: build.mutation<
			AdminImportPreparationResult | ImportErrorResponse,
			{ schoolId: number; data: AdminImportStudent[] }
		>({
			query: ({ schoolId, data }) => ({
				url: `AdminRosterImport/PrepareImportStudents?schoolId=${encodeURIComponent(
					schoolId
				)}`,
				method: "POST",
				body: data,
			}),
		}),
		importStudents: build.mutation<
			AdminImportResult | ImportErrorResponse,
			{ schoolId: number; data: AdminImportStudent[] }
		>({
			query: ({ schoolId, data }) => ({
				url: `AdminRosterImport/ImportStudents?schoolId=${encodeURIComponent(schoolId)}`,
				method: "POST",
				body: data,
			}),
			invalidatesTags: [
				"District",
				"School",
				"Schools",
				"CourseInfo",
				"UserInfo",
				"UserList",
				"SeatCount",
			],
		}),
	}),
});

export const {
	useDistrictOptionsForRosterImportQuery,
	useLazySchoolOptionsForRosterImportQuery,
	useUploadFileMutation,
	useImportCoursesMutation,
	useImportTeachersMutation,
	usePrepareImportStudentsMutation,
	useImportStudentsMutation,
} = rosterImportApiSlice;
